import React, { useCallback, useEffect, useState } from "react";
import { Award, Loader2 } from "lucide-react";
import { getGoldCandidates, promoteGold } from "../../lib/uploadsApi";
import { useRole } from "../../lib/role.jsx";
import { num } from "../pipeline/Layer";
import { scoreTone } from "./ReadinessLayers";
import { cn } from "../../lib/utils";

const btn = "inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-[12px] font-medium bg-primary text-primary-fg hover:opacity-90 disabled:opacity-45 transition-opacity";

function Ready({ t, canEdit, busy, onCertify }) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`gold-prompt-ready-${t.type}`}>
      <span className="font-medium text-[13px] min-w-[180px]">{t.label}</span>
      <span className={cn("font-semibold font-mono-tabular", scoreTone(t.score))}>{t.score}</span>
      <span className="text-[11.5px] text-muted font-mono-tabular flex-1">{num(t.rows)} rows</span>
      {canEdit ? <button className={btn} disabled={!!busy} onClick={() => onCertify(t)} data-testid={`gold-prompt-certify-${t.type}`}>
        {busy === t.type ? <Loader2 size={12} className="animate-spin" /> : <Award size={12} />}Certify Gold</button>
        : <span className="text-[11.5px] text-muted">The IT Head certifies</span>}
    </div>
  );
}

function Near({ t }) {
  return (
    <div className="py-2 border-b border-border/60 last:border-0 space-y-1" data-testid={`gold-prompt-near-${t.type}`}>
      <div className="flex flex-wrap items-baseline gap-3"><span className="font-medium text-[13px] min-w-[180px]">{t.label}</span>
        <span className={cn("font-semibold font-mono-tabular", scoreTone(t.score))}>{t.score}</span>
        {t.points_short > 0 && <span className="text-[11.5px] text-warning">{t.points_short} point{t.points_short === 1 ? "" : "s"} short</span>}</div>
      <ul className="text-[12px] list-disc pl-5 space-y-0.5">{t.missing.map(x => <li key={x}>{x}</li>)}
        {t.drags.length > 0 && <li className="text-muted">Biggest drags: {t.drags.join("; ")}</li>}</ul>
    </div>
  );
}

// Suggested for Gold: Silver tables that pass every Gold check now (one-click certify for the IT Head) and near misses with what's missing.
export default function GoldPrompt({ onChanged }) {
  const { role } = useRole();
  const canEdit = role?.id === "it_head";
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => getGoldCandidates().then(setC).catch(e => setMsg({ error: e.message })), []);
  useEffect(() => { load(); }, [load]);
  const certify = async (t) => {
    setBusy(t.type);
    try {
      await promoteGold(t.type);
      setMsg({ ok: `${t.label} certified Gold.` });
      setC(x => ({ ...x, ready: x.ready.filter(y => y.type !== t.type) }));
      await load();
      onChanged?.();
    }
    catch (e) { setMsg({ error: e.message }); }
    finally { setBusy(null); }
  };
  return (
    <section className="tile p-4 space-y-3" data-testid="gold-prompt">
      <div className="flex items-center gap-2"><Award size={13} className="text-primary" /><span className="kpi-label">Suggested for Gold</span></div>
      <p className="text-[12.5px] text-muted max-w-3xl">Gold needs a quality score of at least {c?.min_score ?? 80}, no blocking findings, and no model that reads the table left not ready by it.</p>
      {msg?.ok && <div className="text-success text-[12px]" data-testid="gold-prompt-ok">{msg.ok}</div>}
      {msg?.error && <div className="text-critical text-[12px]" data-testid="gold-prompt-error">{msg.error}</div>}
      {!c ? <div className="text-[12.5px] text-muted">Loading…</div> : <>
        <div><div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">Ready now · {c.ready.length}</div>
          {c.ready.length ? c.ready.map(t => <Ready key={t.type} t={t} canEdit={canEdit} busy={busy} onCertify={certify} />)
            : <div className="text-[12px] text-muted" data-testid="gold-prompt-none">No Silver table passes every Gold check yet.</div>}</div>
        {c.near.length > 0 && <div><div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">Near misses · {c.near.length}</div>
          {c.near.map(t => <Near key={t.type} t={t} />)}</div>}
        {c.further > 0 && <div className="text-[11.5px] text-muted" data-testid="gold-prompt-further">{c.further} more table{c.further === 1 ? " is" : "s are"} further away (score below {c.min_score - 10}).</div>}
      </>}
    </section>
  );
}
